import Cookies from "js-cookie";
import { B3_AUTH_COOKIE_NAME, B3OS_LAST_ORG_COOKIE_NAME, B3OS_ORG_COOKIE_NAME } from "@/lib/constants/cookies";
import { getMasqueradeOrgId, getMasqueradeToken } from "@/lib/masquerade";
import { showSessionExpiredToast } from "@/lib/richToasts";
import { toast } from "@/lib/toast";
import { buildLoginRedirectUrl } from "./routes";

export { B3_AUTH_COOKIE_NAME, B3OS_LAST_ORG_COOKIE_NAME, B3OS_ORG_COOKIE_NAME };

/** Error subclass that preserves the API error code and details from the response body. */
export class ApiError extends Error {
  code: number;
  details: Record<string, unknown>[];
  constructor(message: string, code: number, details: Record<string, unknown>[] = []) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.details = details;
  }

  /** Check if error details contain a specific type (e.g., "plan_limit"). */
  hasDetailType(type: string): boolean {
    return this.details.some(d => d.type === type);
  }
}

export const ORG_COOKIE_EXPIRATION_DAYS = 30; // 30 days
export const LAST_ORG_COOKIE_EXPIRATION_DAYS = 365; // 1 year for last org preference
const AUTH_STRATEGY_COOKIE_NAME = "b3-auth-strategy";

function getCookieDomainCandidates(): string[] {
  if (typeof window === "undefined") return [];

  const hostname = window.location.hostname;
  if (!hostname || hostname === "localhost" || /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname)) {
    return [];
  }

  const parts = hostname.split(".").filter(Boolean);
  const candidates = new Set<string>();

  for (let i = 0; i < parts.length - 1; i += 1) {
    const domainParts = parts.slice(i);
    if (domainParts.length < 2) continue;

    const domain = domainParts.join(".");
    candidates.add(domain);
    candidates.add(`.${domain}`);
  }

  return Array.from(candidates);
}

function clearCookie(name: string): void {
  Cookies.remove(name);
  Cookies.remove(name, { path: "/" });

  if (typeof document !== "undefined") {
    document.cookie = `${encodeURIComponent(name)}=; max-age=0`;
    document.cookie = `${encodeURIComponent(name)}=; max-age=0; path=/`;
  }

  for (const domain of getCookieDomainCandidates()) {
    Cookies.remove(name, { domain });
    Cookies.remove(name, { domain, path: "/" });

    if (typeof document !== "undefined") {
      document.cookie = `${encodeURIComponent(name)}=; max-age=0; domain=${domain}`;
      document.cookie = `${encodeURIComponent(name)}=; max-age=0; path=/; domain=${domain}`;
    }
  }
}

/**
 * TODO: Future Refactor - React Query Integration
 *
 * Currently, authenticatedFetch is a plain fetch wrapper. To enable proper query invalidation
 * when switching organizations, we should:
 *
 * 1. Create a React Query-based fetch hook (e.g., useAuthenticatedQuery)
 * 2. Add query keys that include orgId for automatic invalidation
 * 3. Use query tags like ['org', orgId] to invalidate all org-scoped data at once
 * 4. Replace window.location.reload() with queryClient.invalidateQueries({ queryKey: ['org', orgId] })
 *
 * This would allow smooth org switching without full page reloads while ensuring all
 * org-scoped data (workflows, templates, etc.) automatically refetches.
 */

interface AuthenticatedFetchOptions extends RequestInit {
  skipAuth?: boolean;
  /** When true, attach auth headers if available but proceed without them (no redirect/throw) when not logged in. */
  optionalAuth?: boolean;
  showErrorToast?: boolean;
  orgId?: string;
  skipOrgId?: boolean;
}

/**
 * Authenticated fetch wrapper that automatically includes the B3 auth token
 *
 * **Auth modes:**
 * - **Default** — Requires auth. If no token is found, shows an error toast,
 *   redirects to `/login`, and throws. Use for protected endpoints.
 * - **`skipAuth: true`** — Never sends auth headers, even if a token exists.
 *   Use for fully public endpoints that must never receive credentials.
 * - **`optionalAuth: true`** — Best-effort auth. Attaches the token when
 *   available but proceeds without it (no redirect, no throw). Use for
 *   endpoints that return richer data for logged-in users but still work
 *   anonymously (e.g., public workflow pages, SEO routes).
 *
 * @param url - The URL to fetch
 * @param options - Fetch options with additional auth controls
 * @returns Promise with response and typed json() method
 *
 * @example
 * ```ts
 * // Default: requires auth
 * const response = await authenticatedFetch<WorkflowsApiResponse>('/api/workflows');
 * if (response.ok) {
 *   const data = await response.json(); // type: WorkflowsApiResponse
 * }
 * ```
 *
 * @example
 * ```ts
 * // skipAuth: never send credentials (public endpoint)
 * const response = await authenticatedFetch<PublicData>('/api/public', {
 *   skipAuth: true,
 * });
 * ```
 *
 * @example
 * ```ts
 * // optionalAuth: attach token if available, proceed without if not
 * const response = await authenticatedFetch<WorkflowPage>('/api/w/123', {
 *   optionalAuth: true,
 * });
 * ```
 */
export async function authenticatedFetch<T = unknown>(
  url: string,
  options: AuthenticatedFetchOptions = {},
): Promise<Response & { json(): Promise<T> }> {
  const {
    skipAuth = false,
    optionalAuth = false,
    showErrorToast = true,
    orgId,
    skipOrgId = false,
    headers = {},
    ...fetchOptions
  } = options;

  // Dev override: use master key auth for local development
  // Format: "local:<USER_ID>" bypasses JWT validation in b3os-workflow
  const devAuthToken = process.env.NEXT_PUBLIC_DEV_AUTH_TOKEN;

  // Get access token from dev override or cookies
  const accessToken = devAuthToken || Cookies.get(B3_AUTH_COOKIE_NAME);

  const masqueradeToken = getMasqueradeToken();
  const masqueradeOrgId = masqueradeToken ? getMasqueradeOrgId() : undefined;
  const isMasquerading = !!masqueradeToken;

  // Short-circuit if session is known to be fully expired (BetterAuth session gone).
  // This prevents the "request storm" where background polling keeps hammering the
  // backend with a stale token after the session has been confirmed expired.
  if (!isMasquerading && !skipAuth && !optionalAuth && !devAuthToken && accessToken) {
    const { isSessionFullyExpired, redirectToLogin } = await import("@/lib/sessionRefresh");
    if (isSessionFullyExpired()) {
      if (showErrorToast) {
        const currentPath =
          typeof window !== "undefined" ? window.location.pathname + window.location.search : undefined;
        showSessionExpiredToast(currentPath);
      }
      redirectToLogin();
      throw new Error("Session expired. Please log in again.");
    }
  }

  // Check if authentication is required (skip when masquerading — token is the auth)
  if (!isMasquerading && !skipAuth && !optionalAuth && !accessToken) {
    const errorMessage = "Authentication required. Please log in again.";
    if (showErrorToast) {
      const currentPath = typeof window !== "undefined" ? window.location.pathname + window.location.search : undefined;
      showSessionExpiredToast(currentPath);
    }
    // Redirect to login page with current path as redirect param
    if (typeof window !== "undefined") {
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = buildLoginRedirectUrl(currentPath);
    }
    throw new Error(errorMessage);
  }

  // Get org ID from parameter, cookies, or skip
  const selectedOrgId = orgId || (!skipOrgId ? Cookies.get(B3OS_ORG_COOKIE_NAME) : undefined);

  // Handle URL with :orgId path parameter
  let finalUrl = url;
  if (url.includes(":orgId")) {
    // Developer error: URL requires orgId but skipOrgId is active
    if (skipOrgId) {
      throw new Error(
        `Developer error: URL "${url}" contains :orgId path parameter but skipOrgId is true. ` +
          `Either remove :orgId from the URL or set skipOrgId to false.`,
      );
    }

    // User error: No org selected
    if (!selectedOrgId) {
      const errorMessage = "Organization ID required. Please select an organization.";
      if (showErrorToast) {
        toast.error(errorMessage);
      }
      throw new Error(errorMessage);
    }

    finalUrl = url.replace(":orgId", selectedOrgId);
  }

  // Prepare headers as a plain object for easier manipulation
  const requestHeaders: Record<string, string> = {};

  // Copy existing headers
  if (headers) {
    if (headers instanceof Headers) {
      headers.forEach((value, key) => {
        requestHeaders[key] = value;
      });
    } else if (Array.isArray(headers)) {
      headers.forEach(([key, value]) => {
        requestHeaders[key] = value;
      });
    } else {
      Object.assign(requestHeaders, headers);
    }
  }

  if (masqueradeToken && !skipAuth) {
    requestHeaders["X-Masquerade-Token"] = masqueradeToken;
  } else if (!skipAuth && accessToken) {
    requestHeaders["Authorization"] = `Bearer ${accessToken}`;
  }

  // Add X-Org-ID header if URL doesn't contain :orgId path param and orgId is available.
  // When masquerading, prefer the masquerade org ID over the cookie-based one.
  const effectiveOrgId = masqueradeOrgId ?? selectedOrgId;
  if (!url.includes(":orgId") && effectiveOrgId && !skipOrgId) {
    requestHeaders["X-Org-ID"] = effectiveOrgId;
  }

  // Make the fetch request
  const response = await fetch(finalUrl, {
    ...fetchOptions,
    headers: requestHeaders,
  });

  // On 401 during masquerade, clear the dead session instead of attempting BetterAuth refresh.
  if (response.status === 401 && masqueradeToken) {
    const { clearMasqueradeSession } = await import("@/lib/masquerade");
    clearMasqueradeSession();
    throw new Error("Masquerade session expired or revoked.");
  }

  // On 401, attempt silent session refresh and retry once.
  // The BetterAuth session may still be valid even when the Feathers JWT has expired.
  if (response.status === 401 && !skipAuth && !optionalAuth && !devAuthToken) {
    const { attemptSessionRefresh, isSessionFullyExpired } = await import("@/lib/sessionRefresh");
    if (!isSessionFullyExpired()) {
      const refreshed = await attemptSessionRefresh();
      if (refreshed) {
        // Re-read the now-fresh token and retry
        const freshToken = Cookies.get(B3_AUTH_COOKIE_NAME);
        if (freshToken) {
          requestHeaders["Authorization"] = `Bearer ${freshToken}`;
          const retryResponse = await fetch(finalUrl, {
            ...fetchOptions,
            headers: requestHeaders,
          });
          return retryResponse as Response & { json(): Promise<T> };
        }
      }
      // Refresh failed — session fully expired, show toast and redirect to login
      if (showErrorToast) {
        const currentPath =
          typeof window !== "undefined" ? window.location.pathname + window.location.search : undefined;
        showSessionExpiredToast(currentPath);
      }
      const { redirectToLogin } = await import("@/lib/sessionRefresh");
      redirectToLogin();
      throw new Error("Session expired. Please log in again.");
    }
  }

  return response as Response & { json(): Promise<T> };
}

/**
 * Authenticated fetch with automatic JSON parsing
 *
 * @param url - The URL to fetch
 * @param options - Fetch options with additional auth controls
 * @returns Promise<T> - Parsed JSON response
 *
 * @example
 * ```ts
 * interface Workflow {
 *   id: string;
 *   name: string;
 * }
 *
 * const workflows = await authenticatedFetchJson<Workflow[]>('/api/workflows');
 * ```
 */
export async function authenticatedFetchJson<T = unknown>(
  url: string,
  options: AuthenticatedFetchOptions = {},
): Promise<T> {
  const response = await authenticatedFetch<T>(url, options);

  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    let errorCode = 0;
    let errorDetails: Record<string, unknown>[] = [];
    try {
      const body = await response.json();
      if (body?.error?.message) {
        errorMessage = body.error.message;
        errorCode = body.error.code ?? 0;
        errorDetails = Array.isArray(body.error.details) ? body.error.details : [];
      } else if (body?.message) {
        errorMessage = body.message;
        errorCode = body.code ?? 0;
        errorDetails = Array.isArray(body.details) ? body.details : [];
      }
    } catch {
      // response body not JSON, use default message
    }
    throw errorCode ? new ApiError(errorMessage, errorCode, errorDetails) : new Error(errorMessage);
  }

  return response.json();
}

/**
 * Get the currently selected organization ID from cookies
 */
export function getSelectedOrgId(): string | undefined {
  return Cookies.get(B3OS_ORG_COOKIE_NAME);
}

/**
 * Set the selected organization ID in cookies
 * @param orgId - The organization ID to set
 */
export function setSelectedOrgId(orgId: string): void {
  if (!orgId || orgId === "undefined") {
    console.warn("@@B3OS: Attempted to set invalid org ID:", orgId);
    clearSelectedOrgId();
    return;
  }
  // Set cookie to expire in 10 years for long-term persistence
  Cookies.set(B3OS_ORG_COOKIE_NAME, orgId, { expires: ORG_COOKIE_EXPIRATION_DAYS });
}

/**
 * Clear the selected organization ID from cookies
 */
export function clearSelectedOrgId(): void {
  clearCookie(B3OS_ORG_COOKIE_NAME);
}

/**
 * Get the last used organization ID from cookies
 * This persists longer than the selected org and is used for auto-selection on login
 */
export function getLastOrgId(): string | undefined {
  return Cookies.get(B3OS_LAST_ORG_COOKIE_NAME);
}

/**
 * Set the last used organization ID in cookies
 * This is saved whenever a user selects an org, and persists for 1 year
 * @param orgId - The organization ID to remember
 */
export function setLastOrgId(orgId: string): void {
  if (!orgId || orgId === "undefined") {
    console.warn("@@B3OS: Attempted to set invalid last org ID:", orgId);
    return;
  }
  Cookies.set(B3OS_LAST_ORG_COOKIE_NAME, orgId, { expires: LAST_ORG_COOKIE_EXPIRATION_DAYS });
}

/**
 * Clear the last used organization ID from cookies
 */
export function clearLastOrgId(): void {
  clearCookie(B3OS_LAST_ORG_COOKIE_NAME);
}

/**
 * Clear the JWT + active org cookies that drive authenticated routing.
 * This is intentionally defensive: the auth cookie may be set as either a
 * host-only cookie or a domain-scoped cookie (e.g. `.b3os.org`).
 * By default, preserve the last-used org preference so multi-org users can
 * still be auto-routed after signing back in.
 */
export function clearClientSessionCookies(options: { preserveLastOrgPreference?: boolean } = {}): void {
  const { preserveLastOrgPreference = true } = options;

  clearCookie(B3_AUTH_COOKIE_NAME);
  clearSelectedOrgId();
  if (!preserveLastOrgPreference) {
    clearLastOrgId();
  }
  clearCookie(AUTH_STRATEGY_COOKIE_NAME);
  // Clear betterauth session token used for cross-origin linkSocial Bearer auth.
  // Must be removed on logout so a stale token from a previous session cannot be
  // reused by the next user on the same device.
  try {
    localStorage.removeItem("ba_session_token");
  } catch {}
}
